import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as InfoIcon } from '../../../assets/icons/info-icon.svg';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { fetchAccountAllList } from '../../../redux/slices/AccountManagementSlice';
import {
  fetchAllConnectorRating,
  fetchConnectorScopList,
} from '../../../redux/slices/ConnectorRatingSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomDatePicker from '../../../shared-components/custom-date-range/CustomDatePicker';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import SelectField from '../../../shared-components/select-field/SelectField';
import penaltyManagementApi from '../../../utils/api/penalty-management';
import tariffApi from '../../../utils/api/tariff';

interface TariffCalculatorProperties {
  allowType?: boolean;
}
/**
 * Component for tariff calculator
 * @param {any} properties - Tariff calculator properties
 * @returns {any} Tariff calculator component
 */
function TariffCalculator(properties: TariffCalculatorProperties) {
  const { allowType = false } = properties;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('tariff');

  const { connectorRatingsAll, scopeList } = useAppSelector((state) => state.connectorRating);
  const { accountList } = useAppSelector((state) => state.accounts);

  const [showStationInfo, setShowStationInfo] = useState<boolean>(false);
  const [calculatorData, setCalculatorData] = useState({
    sessionStartTime: '',
    sessionEndTime: '',
    connectorRating: '',
    currentType: '',
    powerConsumed: '',
    partner: '',
    station: '',
    country: '',
    state: '',
    city: '',
    time: '',
  });

  const [stationsList, setStationsList] = useState<Array<{ label: string; value: string }>>([]);

  const [calculated, setCalculated] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [results, setResults] = useState<any>({
    priceBreakdown: [
      { componentName: 'Base Price', amount: '0.00 SAR' },
      { componentName: 'Operating Cost', amount: '0.00 SAR' },
    ],
    totalAmount: '0.00 SAR',
  });

  /**
   * Translates component name if it matches known keys
   * @param {string} name - The component name
   * @returns {string} The translated component name
   */
  const getTranslatedComponentName = (name: string): string => {
    if (!name) return name;
    if (name === 'Base Price' || name === 'basePrice' || name === t('basePrice', { defaultValue: 'Base Price' })) {
      return t('basePrice', { defaultValue: 'Base Price' });
    }
    if (name === 'Operating Cost' || name === 'operatingCost' || name === t('operatingCost', { defaultValue: 'Operating Cost' })) {
      return t('operatingCost', { defaultValue: 'Operating Cost' });
    }
    return name;
  };

  /**
   * Get the scope name
   * @param {string} scope - The scope
   * @param {string} scopeId - The scope ID
   * @returns {string} The scope name
   */
  const getScopeName = (scope: string, scopeId: string) => {
    const currentScopeList: any[] = (scopeList as Record<string, any[]> | undefined)?.[scope] || [];
    return currentScopeList.find((scopeItem) => scopeItem.value === scopeId)?.label || '';
  };

  /**
   * Update calculator data
   * @param {string} field - The field to update
   * @param {string} value - The new value
   */
  const updateCalculatorData = (field: string, value: string) => {
    setCalculatorData((previous) => {
      const updated = { ...previous, [field]: value };
      if (field === 'currentType' && previous.currentType !== value) {
        updated.connectorRating = '';
      }
      return updated;
    });

    if (errors[field]) {
      setErrors((previous) => ({ ...previous, [field]: '' }));
    }
    if (field === 'currentType' && errors.connectorRating) {
      setErrors((previous) => ({ ...previous, connectorRating: '' }));
    }

    switch (field) {
      case 'partner':
        penaltyManagementApi.findStationNames(value).then((response) => {
          setStationsList(
            response.data.map((station: any) => ({ label: station.name, value: station._id })) ||
              [],
          );
        });
        break;
      // case 'currentType':
      //   dispatch(fetchAllConnectorRating());
      //   break;
      case 'tariffType':
        if (value === 'custom') {
          dispatch(fetchConnectorScopList({ scope: 'country', scopeId: '' }));
          dispatch(fetchAccountAllList());
        }
        break;
      case 'country':
        dispatch(fetchConnectorScopList({ scope: 'state', scopeId: value }));
        break;
      case 'state':
        dispatch(fetchConnectorScopList({ scope: 'city', scopeId: value }));
        break;
      case 'city':
        break;

      default:
        break;
    }
  };

  /**
   * Handle reset
   */
  const handleReset = () => {
    setCalculatorData({
      sessionStartTime: '',
      sessionEndTime: '',
      connectorRating: '',
      currentType: '',
      powerConsumed: '',
      partner: '',
      station: '',
      country: 'India',
      state: '',
      city: '',
      time: '',
    });
    setErrors({});
    setShowStationInfo(false);
    setResults({
      priceBreakdown: [
        { componentName: 'Base Price', amount: '0.00 SAR' },
        { componentName: 'Operating Cost', amount: '0.00 SAR' },
      ],
      totalAmount: '0.00 SAR',
    });
  };

  /**
   * Validate calculator form fields
   * @returns {boolean} Whether the form is valid
   */
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!calculatorData.sessionStartTime) {
      newErrors.sessionStartTime = t('sessionStartTimeRequired');
    } else {
      const startDate = new Date(calculatorData.sessionStartTime);
      if (Number.isNaN(startDate.getTime())) {
        newErrors.sessionStartTime = t('invalidSessionStartTime');
      }
    }

    if (calculatorData.sessionEndTime) {
      const endDate = new Date(calculatorData.sessionEndTime);
      if (Number.isNaN(endDate.getTime())) {
        newErrors.sessionEndTime = t('invalidSessionEndTime');
      } else if (calculatorData.sessionStartTime) {
        const startDate = new Date(calculatorData.sessionStartTime);
        if (!Number.isNaN(startDate.getTime()) && endDate <= startDate) {
          newErrors.sessionEndTime = t('sessionEndTimeAfterStartTime');
        }
      }
    }

    if (!calculatorData.currentType) {
      newErrors.currentType = t('currentTypeRequired');
    }

    if (!calculatorData.connectorRating) {
      newErrors.connectorRating = t('connectorRatingRequired');
    }

    if (!calculatorData.powerConsumed || String(calculatorData.powerConsumed).trim() === '') {
      newErrors.powerConsumed = t('powerConsumedRequired');
    } else if (Number(calculatorData.powerConsumed) <= 0) {
      newErrors.powerConsumed = t('powerConsumedGreaterThanZero');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle calculate
   */
  const handleCalculate = () => {
    if (!validateForm()) {
      return;
    }

    const scopeLevels = [];
    if (calculatorData.country) {
      scopeLevels.push({
        scope: 'country',
        scopeId: calculatorData.country,
        scopeName: getScopeName('country', calculatorData.country),
      });
    }
    if (calculatorData.state) {
      scopeLevels.push({
        scope: 'state',
        scopeId: calculatorData.state,
        scopeName: getScopeName('state', calculatorData.state),
      });
    }
    if (calculatorData.city) {
      scopeLevels.push({
        scope: 'city',
        scopeId: calculatorData.city,
        scopeName: getScopeName('city', calculatorData.city),
      });
    }

    const startDate = new Date(calculatorData.sessionStartTime);

    const requestData: any = {
      unitsConsumed: calculatorData.powerConsumed,
      startDateUTC: startDate.toISOString(),
      connectorRating: calculatorData.connectorRating,
      currentType: calculatorData.currentType,
    };
    if (calculatorData.sessionEndTime) {
      const endDate = new Date(calculatorData.sessionEndTime);
      if (!Number.isNaN(endDate.getTime())) {
        requestData.endDateUTC = endDate.toISOString();
      }
    }
    if (scopeLevels.length > 0) {
      requestData.scopeLevels = scopeLevels;
    }
    if (calculatorData.partner) {
      requestData.accountCustomId = calculatorData.partner;
    }
    if (calculatorData.station) {
      requestData.stationId = calculatorData.station;
    }

    setLoading(true);
    tariffApi
      .fetchTariffCalculatorData(requestData)
      .then((response) => {
        const data = response.data || {};

        data.priceBreakdown = response?.data?.amountSplit?.map((split: any) => ({
          componentName: split.componentName,
          amount: `${split.contributedAmount.toFixed(2)} ${data.currency}`,
        })) || [
          { componentName: 'Base Price', amount: '0.00 SAR' },
          { componentName: 'Operating Cost', amount: '0.00 SAR' },
        ];
        if (response.data.timeBasedPricing.slotName) {
          data.timeBasePrice = [
            {
              componentName: response?.data?.timeBasedPricing?.slotName || t('additionalCharges'),
              amount: `${response?.data?.timeBasedPricing?.additionalAmount?.toFixed(2)}  ${data.currency}`,
            },
          ];
        }
        data.totalAmount =
          `${response?.data?.totalAmount?.toFixed(2)} ${data.currency}` || '0.00 SAR';
        setResults(data);
        setCalculated(true);
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message:
              error?.response?.data?.message ||
              t('invalidTariffDetails'),
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
        // Optionally, set some error state here to display an error message to the user
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    dispatch(setPageTitle(t('tariffCalculator')));
  }, [dispatch, t]);

  useEffect(() => {
    dispatch(fetchConnectorScopList({ scope: 'country', scopeId: '' }));
    dispatch(fetchAccountAllList());
    if (connectorRatingsAll.length === 0) {
      dispatch(fetchAllConnectorRating());
    }
  }, []);

  return (
    <div className="tariff-calculator-wrapper">
      <PageTitle
        title={t('tariffCalculator')}
        topRightContent={
          <div className="page-title-button-group">
            {!allowType && (
              <MyButton
                label={t('back')}
                buttonType="primary-outline"
                onClickFunc={() => navigate(-1)}
              />
            )}
          </div>
        }
      />
      <p className="page-subtitle">
        {t('tariffCalculatorSubtitle')}
      </p>

      <div className="calculator-layout-columns">
        <div className="calculator-form-card">
          <p className="section-title">{t('calculateTariff')}</p>

          <div className="form-grid-row">
            <CustomDatePicker
              inputLabel={t('sessionStartTime')}
              placeholder="DD/MM/YYYY 00:00:00"
              requiredTime
              name="sessionStartTime"
              value={
                calculatorData.sessionStartTime?.includes(':')
                  ? calculatorData.sessionStartTime
                  : calculatorData.sessionStartTime?.split(' ')[0]
              }
              onChangeValue={(name, value) => updateCalculatorData('sessionStartTime', value)}
              valueRequired
              error={!!errors.sessionStartTime}
              errorMessage={errors.sessionStartTime}
            />
            <CustomDatePicker
              inputLabel={t('sessionEndTime')}
              placeholder="DD/MM/YYYY 00:00:00"
              requiredTime
              name="sessionEndTime"
              value={
                calculatorData.sessionEndTime?.includes(':')
                  ? calculatorData.sessionEndTime
                  : calculatorData.sessionEndTime?.split(' ')[0]
              }
              onChangeValue={(name, value) => updateCalculatorData('sessionEndTime', value)}
              error={!!errors.sessionEndTime}
              errorMessage={errors.sessionEndTime}
            />
          </div>

          <div className="form-grid-row">
            <SelectField
              inputLabel={t('currentType')}
              options={[
                { label: 'AC', value: 'AC' },
                { label: 'DC', value: 'DC' },
              ]}
              name="currentType"
              selectedValue={calculatorData.currentType}
              placeholder={t('chooseCurrentType')}
              onChangeValue={(name, value) => updateCalculatorData('currentType', value)}
              valueRequired
              error={!!errors.currentType}
              errorMessage={errors.currentType}
            />
            <SelectField
              inputLabel={t('connectorRatingKw')}
              options={connectorRatingsAll
                .filter((rating: any) => rating.currentType === calculatorData.currentType)
                .map((rating: any) => ({
                  label: `${rating.currentType} ${rating.capacityKW} KW`,
                  value: rating.capacityKW,
                }))}
              name="connectorRating"
              selectedValue={calculatorData.connectorRating}
              placeholder={t('chooseConnectorRating')}
              onChangeValue={(name, value) => updateCalculatorData('connectorRating', value)}
              valueRequired
              error={!!errors.connectorRating}
              errorMessage={errors.connectorRating}
            />
          </div>

          <div className="form-grid-row single-field">
            <CustomInput
              inputLabel={t('powerConsumedKw')}
              placeholder={t('egPowerConsumedPlaceholder')}
              name="powerConsumed"
              type="number"
              value={calculatorData.powerConsumed}
              onChangeValue={(name, value) => updateCalculatorData('powerConsumed', String(value))}
              valueRequired
              decimalLimit={3}
              maxValue={1000}
              error={!!errors.powerConsumed}
              errorMessage={errors.powerConsumed}
            />
          </div>

          <div className="form-grid-row">
            <div className="select-with-info-label">
              <div className="input-label-with-icon">
                <span>{t('partner')}</span>
                <InfoIcon className="info-icon" />
              </div>
              <SelectField
                options={
                  accountList?.map((account: any) => ({
                    label: account.accountName,
                    value: account.accountCustomId,
                  })) || []
                }
                name="partner"
                selectedValue={calculatorData.partner}
                placeholder={t('choosePartner')}
                onChangeValue={(name, value) => {
                  updateCalculatorData('partner', value);

                  if (value) {
                    setShowStationInfo(false);
                  }
                }}
              />
            </div>
            <div className="select-with-info-label">
              <div className="input-label-with-icon">
                <span>{t('station')}</span>
                <InfoIcon className="info-icon" />
              </div>
              <SelectField
                options={stationsList || []}
                name="station"
                selectedValue={calculatorData.station}
                placeholder={t('chooseStation')}
                onChangeValue={(name, value) => {
                  if (!calculatorData.partner) {
                    setShowStationInfo(true);
                    return;
                  }
                  setShowStationInfo(false);
                  updateCalculatorData('station', value);
                }}
              />
              {showStationInfo && (
                <p className="station-info-message">{t('pleaseSelectPartnerFirst')}</p>
              )}
            </div>
          </div>

          <div className="form-grid-row">
            <SelectField
              inputLabel={t('level1Country')}
              options={
                (scopeList as { country?: Array<{ label: string; value: string }> })?.country || []
              }
              name="country"
              selectedValue={calculatorData.country}
              placeholder={t('chooseCountry')}
              onChangeValue={(name, value) => updateCalculatorData('country', value)}
            />
            <SelectField
              inputLabel={t('level2State')}
              options={
                (scopeList as { state?: Array<{ label: string; value: string }> })?.state || []
              }
              name="state"
              selectedValue={calculatorData.state}
              placeholder={t('chooseState')}
              onChangeValue={(name, value) => updateCalculatorData('state', value)}
            />
          </div>

          <div className="form-grid-row single-field">
            <SelectField
              inputLabel={t('level3City')}
              options={
                (scopeList as { city?: Array<{ label: string; value: string }> })?.city || []
              }
              name="city"
              selectedValue={calculatorData.city}
              placeholder={t('chooseCity')}
              onChangeValue={(name, value) => updateCalculatorData('city', value)}
            />
          </div>

          <div className="calculator-actions-row">
            <MyButton
              label={t('reset')}
              buttonType="primary-outline"
              onClickFunc={handleReset}
              disableButton={loading}
            />
            <MyButton
              label={t('calculate')}
              buttonType="submit"
              onClickFunc={handleCalculate}
              disableButton={loading}
              loading={loading}
            />
          </div>
        </div>

        {calculated && (
          <div className="calculator-results-card">
            <div className="results-grid-split">
              <div className="results-summary-details">
                <p className="section-title">{t('calculateTariff')}</p>

                <div className="result-detail-item">
                  <span className="result-label">{t('tariffApplied')}</span>
                  <span className="result-value bold">{results.tariffName}</span>
                </div>

                <div className="result-detail-item">
                  <span className="result-label">{t('scopeLevel')}</span>
                  <span className="result-value bold">{results.type}</span>
                </div>

                <div className="result-detail-item">
                  <span className="result-label">{t('scopeIdOrName')}</span>
                  <span className="result-value bold">
                    {results.scopeName} ({results.scopeId})
                  </span>
                </div>

                <div className="result-detail-item">
                  <span className="result-label">{t('totalAmount')}</span>
                  <span className="result-value bold highlight-value">{results.totalAmount}</span>
                </div>

                <div className="result-detail-item">
                  <span className="result-label">{t('connectorRatingCapacityKw')}</span>
                  <span className="result-value bold">{results?.connectorRating?.capacityKW}</span>
                </div>
              </div>

              <div className="price-breakdown-card">
                <p className="price-breakdown-title">{t('priceBreakdown')}</p>

                <div className="breakdown-table-header">
                  <span>{t('component')}</span>
                  <span>{t('amount')}</span>
                </div>

                <div className="breakdown-rows-list">
                  {results?.priceBreakdown?.map((item: any, index: number) => (
                    <div key={index} className="breakdown-row-item">
                      <span className="comp-name">{getTranslatedComponentName(item.componentName)}</span>
                      <span className="comp-amt">{item.amount}</span>
                    </div>
                  ))}
                </div>

                {false && (
                  <>
                    <p className="price-breakdown-title">{t('timeBasedPricing')}</p>
                    <div className="breakdown-table-header">
                      <span>{t('component')}</span>
                      <span>{t('amount')}</span>
                    </div>
                    <div className="breakdown-rows-list">
                      {results?.timeBasePrice?.map((item: any, index: number) => (
                        <div key={index} className="breakdown-row-item">
                          <span className="comp-name">{getTranslatedComponentName(item.componentName)}</span>
                          <span className="comp-amt">{item.amount}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                <div className="breakdown-total-footer">
                  <span className="footer-label">{t('totalAmount')}</span>
                  <span className="footer-value">{results.totalAmount}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
TariffCalculator.defaultProps = {
  allowType: false,
};

export default TariffCalculator;
